import Axios, { AxiosError, AxiosInstance } from "axios";
import storage from "../helpers/storage";
import { logApiEnd, logApiStart } from "../utils/performance";
import {
  AuthAxiosRequestConfig,
  isAuthExemptUrl,
  isRefreshUrl,
  logAuth,
  logoutSession,
  refreshAccessToken,
} from "./auth-session";

const API_TIMEOUT_MS = 10_000;

type TimedRequestConfig = AuthAxiosRequestConfig & {
  metadata?: { requestId: string; startMark: number };
};

function authRequestInterceptor(config: TimedRequestConfig) {
  const url = config.url || "";
  if (!isAuthExemptUrl(url)) {
    const token = storage.getToken();
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
  }
  config.headers.Accept = "application/json";

  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const startMark = logApiStart(
    config.method || "get",
    `${config.baseURL || ""}${url}`,
    requestId,
  );
  config.metadata = { requestId, startMark };
  return config;
}

function logResponseEnd(
  config: TimedRequestConfig | undefined,
  status?: number,
  isError = false,
) {
  if (!config?.metadata) return;
  const { requestId, startMark } = config.metadata;
  logApiEnd(
    config.method || "get",
    `${config.baseURL || ""}${config.url || ""}`,
    requestId,
    startMark,
    status,
    isError,
  );
}

async function handleResponseError(
  instance: AxiosInstance,
  error: AxiosError,
) {
  const config = error.config as TimedRequestConfig | undefined;
  logResponseEnd(config, error.response?.status, true);

  if (error.code === "ECONNABORTED") {
    return Promise.reject({
      data: { msg: "Request timed out. Please try again." },
      status: 408,
      timedOut: true,
    });
  }

  const status = error.response?.status;
  const url = config?.url || "";

  if (status === 401 && config) {
    if (isRefreshUrl(url)) {
      logAuth(
        "request skipped refresh because it is refresh endpoint — logging out",
      );
      logoutSession("refresh endpoint returned 401");
      return Promise.reject(error.response ?? error);
    }

    if (isAuthExemptUrl(url)) {
      logAuth("request skipped refresh because it is auth endpoint", { url });
      return Promise.reject(error.response ?? error);
    }

    if (!storage.getRefreshToken()) {
      logoutSession("no refresh token on 401");
      return Promise.reject(error.response ?? error);
    }

    if (config._retry) {
      logoutSession("401 after retry");
      return Promise.reject(error.response ?? error);
    }

    config._retry = true;

    try {
      const newAccess = await refreshAccessToken();
      config.headers = config.headers ?? {};
      config.headers.authorization = `Bearer ${newAccess}`;
      const retryResponse = await instance.request(config);
      return retryResponse.data;
    } catch (refreshError) {
      return Promise.reject(error.response ?? refreshError);
    }
  }

  return Promise.reject(error.response ?? error);
}

function attachClient(instance: AxiosInstance) {
  instance.defaults.timeout = API_TIMEOUT_MS;
  instance.interceptors.request.use(authRequestInterceptor);
  instance.interceptors.response.use(
    (response) => {
      logResponseEnd(response.config as TimedRequestConfig, response.status);
      return response.data;
    },
    (error: AxiosError) => handleResponseError(instance, error),
  );
  return instance;
}

const httpClient = attachClient(
  Axios.create({
    baseURL: process.env.REACT_APP_API_URL + "/admin-apis",
  }),
);

export const httpAppClient = attachClient(
  Axios.create({
    baseURL: process.env.REACT_APP_API_URL + "/apis",
  }),
);

export default httpClient;
